import { AI_ALLOWED_LICENSES, FREE_RANGE_TOKEN } from './buildSearchParams'
import {
  ADVANCED_FILTER,
  ASSET_TYPE,
  CONTENT_FILTER,
  CONTENT_SOURCE,
  LICENSE_TYPE,
  PERSONAL_FILTER,
  PRICE_RANGE,
  POLYGON_RANGE,
  PURCHASE_FILTER,
  SORT_BY,
} from './constants'
import { findStyleLabel } from '../Shared/StyleContent/mock'

const PRICE_BUCKETS = new Set(Object.values(PRICE_RANGE))
const POLYGON_PRESETS = new Set(Object.values(POLYGON_RANGE))
const ADVANCED_FILTERS = new Set(Object.values(ADVANCED_FILTER))
const CONTENT_FILTERS = new Set(Object.values(CONTENT_FILTER))
const SORT_VALUES = new Set(Object.values(SORT_BY))
const LICENSE_VALUES = new Set(
  Object.values(LICENSE_TYPE).filter((value) => value !== LICENSE_TYPE.ALL)
)

const CUSTOM_RANGE_PARSER = /^range_(\d+)_(\d+)$/
const MORE_THAN_PARSER = /^more_than_(\d+)$/

// Boolean query params buildSearchParams emits as '1', keyed by their store field.
const BOOLEAN_PARAMS = {
  sale_off: 'onSale',
  subscribed: 'withSubscription',
  animated: 'animated',
  pbr: 'pbr',
  rigged: 'rigged',
  with_renders: 'withRenders',
  with_makes: 'withMakes',
  cgt_verified: 'threeDPrintVerified',
}

const decode = (part) => {
  try {
    return decodeURIComponent(part.replace(/\+/g, ' '))
  } catch (e) {
    return part
  }
}

// Minimal parser for the Rails `to_query` format the backend echoes into the canonical URL
// (`key=v`, `key[]=v` arrays, `price[from]=0` nested scalars). URLSearchParams is not available
// in the SSR runtime, and this must parse identically on server and client.
const parseQuery = (search) => {
  const params = {}
  search.split('&').filter(Boolean).forEach((pair) => {
    const eq = pair.indexOf('=')
    const key = decode(eq === -1 ? pair : pair.slice(0, eq))
    const value = eq === -1 ? '' : decode(pair.slice(eq + 1))
    if (key.endsWith('[]')) {
      const base = key.slice(0, -2)
      params[base] = params[base] || []
      params[base].push(value)
    } else {
      params[key] = value
    }
  })
  return params
}

// parseQuery only builds arrays for `key[]=` params. A hand-written or legacy URL can carry the
// scalar form (`?price_range=range_2_10`), so every array-capable param is normalized before it is
// iterated - otherwise hydration throws on `String.filter`/`forEach` and takes the SSR render with it.
const toArray = (value) => {
  if (value === undefined || value === '') return []
  return Array.isArray(value) ? value : [value]
}

// Splits range tokens into { buckets, min, max }: tokens found in `known` are preset buckets,
// `range_MIN_MAX` / `more_than_MIN` are the custom range (mirrors what buildSearchParams emits).
const parseRangeTokens = (tokens, known) => {
  const result = { buckets: [], min: undefined, max: undefined }
  tokens.forEach((token) => {
    if (known.has(token)) {
      result.buckets.push(token)
      return
    }
    const range = token.match(CUSTOM_RANGE_PARSER)
    const moreThan = token.match(MORE_THAN_PARSER)
    if (range) {
      if (Number(range[1]) > 0) result.min = Number(range[1])
      result.max = Number(range[2])
    } else if (moreThan) {
      result.min = Number(moreThan[1])
    }
  })
  return result
}

const hydratePrice = (params) => {
  const state = {}
  // Free travels as `range_0_0` inside price_range when combined with other ranges (see
  // buildSearchParams FREE_RANGE_TOKEN), or as price={0,0} when selected alone. Pull the token
  // out before generic parsing - CUSTOM_RANGE_PARSER would otherwise read it as maxPrice=0.
  const priceRange = toArray(params.price_range)
  const tokens = priceRange.filter((token) => token !== FREE_RANGE_TOKEN)
  const hasFreeToken = tokens.length !== priceRange.length
  const { buckets, min, max } = parseRangeTokens(tokens, PRICE_BUCKETS)

  // `free=1` is backend-derived: SearchKeywordToFilter canonicalizes "free" keywords (and ?free=1
  // links) into it and SSR returns only free models. It must hydrate into the Free bucket or the
  // next fetch silently drops the constraint and returns paid models.
  if (hasFreeToken || params.free === '1' || (params['price[from]'] === '0' && params['price[to]'] === '0')) {
    buckets.unshift(PRICE_RANGE.FREE)
  }

  if (buckets.length > 0) state.priceRanges = buckets
  if (min !== undefined) state.minPrice = min
  if (max !== undefined) state.maxPrice = max
  return state
}

const hydratePolygon = (params) => {
  const state = {}
  const { buckets, min, max } = parseRangeTokens(toArray(params.polygon_range), POLYGON_PRESETS)

  if (buckets.length > 0) state.polygonRanges = buckets
  if (min !== undefined) state.minPolygon = min
  if (max !== undefined) state.maxPolygon = max
  return state
}

const hydrateLicenses = (params) => {
  const state = {}
  const advancedFilters = []
  const licenseTypes = []
  let hasEditorialRoyaltyFree = false
  toArray(params.licenses).forEach((token) => {
    if (token === 'ai_generated_only') {
      state.contentSource = CONTENT_SOURCE.AI_GENERATED
    } else if (token === 'exclude_ai_generated') {
      state.contentSource = CONTENT_SOURCE.HUMAN_CREATED
    } else if (token === 'include_ai_generated') {
      state.contentSource = CONTENT_SOURCE.ALL
    } else if (token === 'exclude_3d_print') {
      state.assetType = ASSET_TYPE.THREE_D_ONLY
    } else if (token === PERSONAL_FILTER.WISH_LIST_ONLY) {
      state.personalFilter = PERSONAL_FILTER.WISH_LIST_ONLY
    } else if (token === PERSONAL_FILTER.LIKED_MODELS_ONLY) {
      state.personalFilter = PERSONAL_FILTER.LIKED_MODELS_ONLY
    } else if (token === 'hide_owned_models') {
      state.purchaseFilter = PURCHASE_FILTER.EXCLUDE_PURCHASED_MODELS
    } else if (token === 'owned_models') {
      state.purchaseFilter = PURCHASE_FILTER.PURCHASED_MODELS_ONLY
    } else if (CONTENT_FILTERS.has(token)) {
      state.contentFilter = token
    } else if (token === 'editorial_royalty_free') {
      // Only valid as part of AI_ALLOWED_LICENSES (no UI checkbox of its own); tracked separately
      // so the full-set check below can recognize the "All Licenses + AI Usage" combination.
      hasEditorialRoyaltyFree = true
    } else if (ADVANCED_FILTERS.has(token)) {
      advancedFilters.push(token)
    } else if (LICENSE_VALUES.has(token)) {
      licenseTypes.push(token)
    }
    // Unknown tokens are dropped: re-emitting them would let arbitrary URL input round-trip
    // into every subsequent request.
  })

  if (advancedFilters.length > 0) state.advancedFilters = advancedFilters

  // The exact AI-friendly set is what buildSearchParams emits for "All Licenses + AI Usage
  // Allowed"; restore that state (licenses stays the default ['all']) instead of pinning the
  // individual checkboxes, so the round-trip preserves what the user actually selected.
  const isAiAllowedSet = hasEditorialRoyaltyFree &&
    licenseTypes.length === AI_ALLOWED_LICENSES.length - 1 &&
    AI_ALLOWED_LICENSES.every(
      (value) => value === 'editorial_royalty_free' || licenseTypes.includes(value)
    )
  if (isAiAllowedSet) {
    state.aiUsageAllowed = true
  } else if (licenseTypes.length > 0) {
    state.licenses = licenseTypes
    // The AI toggle swaps which value set the checkboxes use (plain vs `_no_ai`), so it must be
    // seeded to match the hydrated values or the selection would render unchecked.
    state.aiUsageAllowed = licenseTypes.some((value) => !value.endsWith('_no_ai'))
  }
  return state
}

/**
 * Inverse of buildSearchParams: maps the canonical listing URL's query params back to SearchPage
 * store state, so landing on a filtered URL seeds the filters that produced it. Later fetches then
 * rebuild the same params instead of silently dropping them.
 *
 * Parses only params buildSearchParams can emit; everything else (keywords, page, per_page,
 * unknown keys) is ignored. `keywords` intentionally stays URL-only (see Store/actions/results.js).
 *
 * @param {string} url - Path with query string (the backend-provided canonical `url` prop)
 * @returns {Object} Partial store state; empty when the URL carries no supported filters
 */
const hydrateStateFromUrl = (url = '') => {
  const queryIndex = url.indexOf('?')
  if (queryIndex === -1) return {}

  const params = parseQuery(url.slice(queryIndex + 1))
  const state = {
    ...hydratePrice(params),
    ...hydratePolygon(params),
    ...hydrateLicenses(params),
  }

  if (params.printable === '1') state.assetType = ASSET_TYPE.THREE_D_PRINT_ONLY
  const fileTypes = toArray(params.file_types)
  if (fileTypes.length > 0) state.fileTypes = fileTypes
  // at_style carries lowercase ES tokens; the store keeps display labels ("sci-fi" -> "Sci-Fi").
  // Tokens outside the known style lists are dropped like any other unknown URL input.
  const atStyle = toArray(params.at_style)
  if (atStyle.length > 0) {
    const styles = atStyle.map(findStyleLabel).filter(Boolean)
    if (styles.length > 0) state.styles = styles
  }
  if (params.suggested_keywords) state.tags = params.suggested_keywords.split(',').filter(Boolean)
  // The within-results input only exists inside the secondary bar, which renders nothing on
  // "All assets" (SecondaryFilters returns null) - mirroring setAssetType, which clears the
  // keyword when that control disappears. Hydrating it without an asset type would apply an
  // invisible term (hand-written /preview-search?within_keywords=wood) that no control shows.
  const withinKeywords = toArray(params.within_keywords).join(' ')
  if (withinKeywords && state.assetType && state.assetType !== ASSET_TYPE.ALL) {
    state.keyword = withinKeywords
  }
  if (SORT_VALUES.has(params.sort_by)) state.sortBy = params.sort_by

  Object.entries(BOOLEAN_PARAMS).forEach(([param, field]) => {
    if (params[param] === '1') state[field] = true
  })

  return state
}

export default hydrateStateFromUrl
